import { MUSIC, MUSIC_IDS, MUSIC_VOLUME_IDS, type MusicId, type MusicVolume } from "../game/music";
import {
	bestFor,
	type NightmareScore,
	type NightmareSort,
	nightmareTop,
	type Settings,
} from "../game/settings";
import { SONG_IDS, SONGS, type SongId } from "../game/songs";
import {
	BAT_POINTS,
	DIFFICULTIES,
	type Difficulty,
	EXTRAS,
	type Extras,
	GHOST_POINTS,
	GOLDEN_POINTS,
	MISS_PENALTY,
	NIGHTMARE_LIVES,
	POINTS,
	ROUND_SECONDS,
	STREAK_EVERY,
	STREAK_MODES,
	type StreakMode,
	streakBonus,
	TRICK_PENALTY,
} from "../sim";
import { el } from "./dom";

/** The menu edits the settings in place; the actions save them. */
export type MenuModel = Settings;

export interface MenuActions {
	onSound(on: boolean): void;
	/** Background music or its volume changed. */
	onMusic(): void;
	/** Round length, difficulty or Minuspoäng changed. */
	onChange(): void;
	onStart(): void;
	/** "❓ Så spelar du": the rules screen. */
	onRules(): void;
	/** "🏆 Topplista": Mardröm's list. */
	onScores(): void;
}

const DIFFICULTY_LABEL: Record<Difficulty, string> = {
	calm: "🐢 Lugn",
	normal: "🐇 Normal",
	fast: "⚡ Snabb",
};

export interface Result {
	score: number;
	stars: 1 | 2 | 3;
	best: number;
	newBest: boolean;
	/** The round's numbers, only with Svit on (the small ones don't need them). */
	stats: RoundStats | null;
	/** Mardröm's time and records; such a round has no stars. */
	nightmare?: { ms: number; bestMs: number; newTime: boolean; newScore: boolean };
}

export interface RoundStats {
	/** Share of the lights (trick lights not counted) that were collected, 0–100; null if none. */
	hitPercent: number | null;
	longestStreak: number;
	misclicks: number;
	/** Trick lights tapped; shown only with Minuspoäng. */
	tricked: number | null;
}

const EXTRAS_LABEL: Record<Extras, string> = {
	both: "🦇 👻 Båda",
	bats: "🦇 Fladdermöss",
	ghosts: "👻 Spöken",
	none: "Inga",
};
const MUSIC_VOLUME_LABEL: Record<MusicVolume, string> = {
	low: "🔈 Låg",
	mid: "🔉 Mellan",
	high: "🔊 Hög",
};
const STREAK_LABEL: Record<StreakMode, string> = { off: "Av", mild: "Mild", strict: "Sträng" };

/** The game-over buttons wake up after this long, so frantic tapping at the end doesn't skip. */
export const OVER_BUTTON_DELAY_MS = 1200;

/**
 * The HTML layer over the canvas: menu popup, countdown, HUD and game over, in Swedish with
 * big buttons and icons for small children. `body[data-screen]` = menu | playing | over tells
 * e2e tests and CSS what is showing.
 */
/** Time alive as "m:ss". */
export function formatTime(ms: number): string {
	const total = Math.floor(ms / 1000);
	return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

export class Overlay {
	private screen: HTMLElement | null = null;
	private readonly hud: HTMLElement;
	private readonly score: HTMLElement;
	/** "🔥 7": the current streak, while Svit is on. */
	private readonly streak: HTMLElement;
	/** "❤️ 7": Mardröm's lives. */
	private readonly lives: HTMLElement;
	private readonly time: HTMLElement;
	private readonly timeText: HTMLElement;
	private readonly timeBar: HTMLElement;
	private readonly countdown: HTMLElement;
	/** Which release is running, for the grown-ups: bottom right while the menu shows. */
	private readonly version: HTMLElement;

	constructor(private readonly root: HTMLElement) {
		this.score = el("span", { class: "hud-score-value" }, "0");
		this.timeText = el("span", { class: "hud-time-value" }, "");
		this.timeBar = el("span", { class: "hud-time-fill" });
		this.time = el(
			"div",
			{ class: "hud-time", role: "timer", "aria-label": "Tid kvar" },
			el("span", { class: "hud-time-bar", "aria-hidden": "true" }, this.timeBar),
			this.timeText,
		);
		this.streak = el("span", { class: "hud-streak", "aria-label": "Svit", hidden: true });
		this.lives = el("span", { class: "hud-lives", "aria-label": "Liv", hidden: true });
		this.hud = el(
			"div",
			{ class: "hud", hidden: true },
			el(
				"div",
				{ class: "hud-left" },
				el("div", { class: "hud-score", "aria-label": "Poäng" }, "⭐ ", this.score),
				this.streak,
				this.lives,
			),
			this.time,
		);
		this.countdown = el("div", { class: "countdown", "aria-live": "assertive", hidden: true });
		this.version = el("p", { class: "app-version", hidden: true }, `v${__APP_VERSION__}`);
		root.append(this.hud, this.countdown, this.version);
	}

	/**
	 * The menu: title, the options on pages behind big ◀ ▶ arrows (Spel: time, pace, Mardröm; Utmaning:
	 * Minuspoäng, Svit, Bonus; Ljud: sound, hit sound, vibration; Musik: music, volume), one dropdown per choice (the phone's own picker), and
	 * "Starta!" with the record below, always in view. Swiping the options also turns the page.
	 * `canVibrate`: offer the vibration choice (only where the device can vibrate).
	 */
	showMenu(model: MenuModel, actions: MenuActions, canVibrate = false): void {
		const best = el("p", { class: "best" });
		const showBest = () => {
			if (model.nightmare) {
				const time = nightmareTop(model.nightmareScores, "time")[0];
				const score = nightmareTop(model.nightmareScores, "score")[0];
				best.textContent =
					time && score ? `Rekord: ${formatTime(time.ms)} · ${score.score} ⭐` : "";
				return;
			}
			const b = bestFor(model);
			best.textContent = b > 0 ? `Rekord: ${b} ⭐` : "";
		};
		/** Mardröm has its own rules: the choices it ignores are greyed out. */
		const roundChoices: HTMLSelectElement[] = [];
		const syncRoundChoices = () => {
			for (const select of roundChoices) select.disabled = model.nightmare;
		};
		const roundChoice = (label: HTMLElement) => {
			const select = label.querySelector("select");
			if (select) roundChoices.push(select);
			return label;
		};
		const onOff = [
			{ value: true, text: "På" },
			{ value: false, text: "Av" },
		];
		const volume = option(
			"🔉",
			"Volym",
			MUSIC_VOLUME_IDS.map((v) => ({ value: v, text: MUSIC_VOLUME_LABEL[v] })),
			model.musicVolume,
			(v) => {
				model.musicVolume = v;
				actions.onMusic();
			},
		);
		const pages: { title: string; options: HTMLElement[] }[] = [
			{
				title: "🎮 Spel",
				options: [
					roundChoice(
						option(
							"⏱️",
							"Tid",
							ROUND_SECONDS.map((s) => ({ value: s, text: `${s} sekunder` })),
							model.seconds,
							(s) => {
								model.seconds = s;
							},
						),
					),
					roundChoice(
						option(
							"🏃",
							"Fart",
							DIFFICULTIES.map((d) => ({ value: d, text: DIFFICULTY_LABEL[d] })),
							model.difficulty,
							(d) => {
								model.difficulty = d;
							},
						),
					),
					option("💀", "Mardröm", onOff, model.nightmare, (on) => {
						model.nightmare = on;
						syncRoundChoices();
					}),
				],
			},
			{
				title: "🏆 Utmaning",
				options: [
					roundChoice(
						option("➖", "Minuspoäng", onOff, model.missPenalty, (on) => {
							model.missPenalty = on;
						}),
					),
					roundChoice(
						option(
							"🔥",
							"Svit",
							STREAK_MODES.map((m) => ({ value: m, text: STREAK_LABEL[m] })),
							model.streakMode,
							(m) => {
								model.streakMode = m;
							},
						),
					),
					roundChoice(
						option(
							"🎁",
							"Bonus",
							EXTRAS.map((e) => ({ value: e, text: EXTRAS_LABEL[e] })),
							model.extras,
							(e) => {
								model.extras = e;
							},
						),
					),
				],
			},
			{
				title: "🔊 Ljud",
				options: [
					option("🔊", "Ljud", onOff, model.sound, (on) => {
						model.sound = on;
						actions.onSound(on);
					}),
					option(
						"🎵",
						"Träffljud",
						[
							{ value: null, text: "🔔 Pling" },
							...SONG_IDS.map((id) => ({ value: id, text: `🎵 ${SONGS[id].name}` })),
						],
						model.song,
						(song: SongId | null) => {
							model.song = song;
						},
					),
					...(canVibrate
						? [
								option("📳", "Vibration", onOff, model.vibrate, (on) => {
									model.vibrate = on;
								}),
							]
						: []),
				],
			},
			{
				title: "🎶 Musik",
				options: [
					option(
						"🎶",
						"Musik",
						[
							{ value: null, text: "Av" },
							...MUSIC_IDS.map((id) => ({ value: id, text: `💀 ${MUSIC[id].name}` })),
						],
						model.music,
						(music: MusicId | null) => {
							model.music = music;
							syncVolume();
							actions.onMusic();
						},
					),
					volume,
				],
			},
		];
		/** Volym only matters while music plays. */
		function syncVolume() {
			const select = volume.querySelector("select");
			if (select) select.disabled = model.music === null;
		}

		/** A labelled dropdown; a change saves the settings and updates the record shown. */
		function option<T>(
			icon: string,
			label: string,
			choices: readonly { value: T; text: string }[],
			current: T,
			set: (value: T) => void,
		): HTMLElement {
			const select = el("select", { "aria-label": label });
			for (const [i, choice] of choices.entries()) {
				const opt = el("option", { value: String(i) }, choice.text);
				opt.selected = choice.value === current;
				select.append(opt);
			}
			select.addEventListener("change", () => {
				const choice = choices[select.selectedIndex];
				if (!choice) return;
				set(choice.value);
				showBest();
				actions.onChange();
			});
			return el("label", { class: "option" }, el("span", {}, `${icon} ${label}`), select);
		}

		const pageEls = pages.map((page, i) =>
			el(
				"div",
				{
					class: "page",
					role: "group",
					"aria-label": page.title.slice(page.title.indexOf(" ") + 1),
					id: `menu-page-${i}`,
				},
				...page.options,
			),
		);
		const title = el("h2", { "aria-live": "polite" });
		const dots = pages.map((page, i) =>
			el("button", {
				class: "dot",
				type: "button",
				"aria-label": `Sida ${i + 1}: ${page.title.slice(page.title.indexOf(" ") + 1)}`,
				onclick: () => turnTo(i),
			}),
		);
		let current = 0;
		const turnTo = (index: number) => {
			current = (index + pages.length) % pages.length;
			title.textContent = pages[current]?.title ?? "";
			for (const [i, page] of pageEls.entries()) page.inert = i !== current;
			for (const [i, dot] of dots.entries())
				dot.setAttribute("aria-current", String(i === current));
		};
		const prev = el(
			"button",
			{
				class: "btn arrow",
				type: "button",
				"aria-label": "Föregående sida",
				onclick: () => turnTo(current - 1),
			},
			"◀",
		);
		const next = el(
			"button",
			{
				class: "btn arrow",
				type: "button",
				"aria-label": "Nästa sida",
				onclick: () => turnTo(current + 1),
			},
			"▶",
		);
		const pagesEl = el("div", { class: "pages" }, ...pageEls);
		// A sideways swipe over the options turns the page too.
		let swipeX: number | null = null;
		pagesEl.addEventListener("pointerdown", (ev) => {
			swipeX = ev.clientX;
		});
		pagesEl.addEventListener("pointerup", (ev) => {
			if (swipeX === null) return;
			const dx = ev.clientX - swipeX;
			swipeX = null;
			if (Math.abs(dx) > 50) turnTo(current + (dx < 0 ? 1 : -1));
		});
		turnTo(0);

		const start = el(
			"button",
			{ class: "btn start", type: "button", onclick: () => actions.onStart() },
			"▶ Starta!",
		);
		const rules = el(
			"button",
			{ class: "btn help", type: "button", onclick: () => actions.onRules() },
			"❓ Så spelar du",
		);
		const more = el(
			"div",
			{ class: "menu-more" },
			el(
				"button",
				{ class: "btn", type: "button", onclick: () => actions.onScores() },
				"🏆 Topplista",
			),
		);
		syncRoundChoices();
		syncVolume();
		showBest();
		this.show(
			"menu",
			el(
				"div",
				{ class: "screen menu", role: "dialog", "aria-label": "Halloweenstaden" },
				el("h1", {}, "🎃 Halloweenstaden"),
				el("p", { class: "intro" }, "Tryck på lamporna som tänds!"),
				el(
					"div",
					{ class: "pager" },
					prev,
					el("div", { class: "pager-title" }, title, el("div", { class: "dots" }, ...dots)),
					next,
				),
				pagesEl,
				start,
				more,
				best,
				rules,
				el("p", { class: "rotate-hint" }, "📱 Vrid skärmen för större bild"),
			),
			start,
		);
	}

	/**
	 * What does what, for the grown-ups and older children: points, bats, streaks and, with
	 * Minuspoäng, what costs points. The numbers come from the sim's constants.
	 */
	showRules(onBack: () => void): void {
		const row = (icon: string | Node, text: string, points: string, kind: "plus" | "minus") =>
			el(
				"li",
				{ class: "rule" },
				el("span", { class: "rule-icon", "aria-hidden": "true" }, icon),
				el("span", { class: "rule-text" }, text),
				el("span", { class: `rule-points ${kind}` }, points),
			);
		const back = el(
			"button",
			{ class: "btn start", type: "button", onclick: onBack },
			"↩ Tillbaka",
		);
		this.show(
			"rules",
			el(
				"div",
				{ class: "screen rules", role: "dialog", "aria-label": "Så spelar du" },
				el("h1", {}, "❓ Så spelar du"),
				el(
					"ul",
					{ class: "rule-list plus" },
					row("💡", "Tryck på en lampa som lyser", `+${POINTS}`, "plus"),
					row("⭐", "Guldlampa", `+${GOLDEN_POINTS}`, "plus"),
					row("🦇", "Fånga fladdermusen", `+${BAT_POINTS}`, "plus"),
					row("👻", "Fånga spöket", `+${GHOST_POINTS}`, "plus"),
				),
				el("p", { class: "rule-note" }, "🎁 Under Bonus i menyn väljer du om de ska komma."),
				el(
					"div",
					{ class: "rule-streak" },
					el("h2", {}, "🔥 Med Svit på"),
					el(
						"ul",
						{ class: "rule-list" },
						...[1, 2, 3].map((n) =>
							row(
								"🔥",
								`${n * STREAK_EVERY} träffar i rad`,
								`+${streakBonus(n * STREAK_EVERY)}${n === 3 ? " …" : ""}`,
								"plus",
							),
						),
					),
					el(
						"p",
						{ class: "rule-note" },
						`Varje ${STREAK_EVERY} i rad ger mer. Mild: en miss bryter sviten. Sträng: även en lampa som slocknar. Efter rundan visas hur många lampor du träffade, längsta sviten och missarna.`,
					),
				),
				el(
					"div",
					{ class: "rule-minus" },
					el("h2", {}, "➖ Med Minuspoäng på"),
					el(
						"ul",
						{ class: "rule-list" },
						row("🌑", "Tryck där det är mörkt", `−${MISS_PENALTY}`, "minus"),
						row(trickIcon(), "Trolllampa (grön och taggig)", `−${TRICK_PENALTY}`, "minus"),
					),
				),
				el(
					"p",
					{ class: "rule-note general" },
					"Poängen blir aldrig mindre än 0. 🐢 Lugn: färre och långsammare lampor. ⚡ Snabb: fler och snabbare.",
				),
				el(
					"div",
					{ class: "rule-nightmare" },
					el("h2", {}, "💀 Mardröm"),
					el(
						"p",
						{ class: "rule-note" },
						`Ingen tidsgräns, men det går fortare och fortare. Du har ${NIGHTMARE_LIVES} liv: en miss, en trolllampa eller en lampa som slocknar kostar ett liv.`,
					),
				),
				back,
			),
			back,
		);
	}

	/** Mardröm's list, with the two sort orders. */
	showScores(scores: readonly NightmareScore[], onBack: () => void): void {
		const body = el("div", { class: "scores-body" });
		const sorts: { sort: NightmareSort; label: string; button?: HTMLButtonElement }[] = [
			{ sort: "time", label: "⏱️ Längst tid" },
			{ sort: "score", label: "⭐ Flest poäng" },
		];
		const showSort = (sort: NightmareSort) => {
			for (const s of sorts) s.button?.setAttribute("aria-pressed", String(s.sort === sort));
			body.replaceChildren(scoreTable(nightmareTop(scores, sort)));
		};
		for (const s of sorts) {
			s.button = el(
				"button",
				{ class: "btn", type: "button", onclick: () => showSort(s.sort) },
				s.label,
			);
		}
		const back = el(
			"button",
			{ class: "btn start", type: "button", onclick: onBack },
			"↩ Tillbaka",
		);
		showSort("time");
		this.show(
			"scores",
			el(
				"div",
				{ class: "screen scores", role: "dialog", "aria-label": "Topplista" },
				el("h1", {}, "🏆 Topplista"),
				el("p", { class: "intro" }, "💀 Mardröm"),
				el("div", { class: "row" }, ...sorts.flatMap((s) => s.button ?? [])),
				body,
				back,
			),
			back,
		);
	}

	showOver(result: Result, onAgain: () => void, onMenu: () => void): void {
		const again = el(
			"button",
			{ class: "btn start", type: "button", disabled: true },
			"↻ Spela igen",
		);
		const menu = el("button", { class: "btn", type: "button", disabled: true }, "Meny");
		again.addEventListener("click", onAgain);
		menu.addEventListener("click", onMenu);
		const { nightmare } = result;
		const heading = nightmare ? "Mardrömmen är slut!" : "Tiden är slut!";
		const stars = el(
			"p",
			{ class: "stars", "aria-label": `${result.stars} av 3 stjärnor` },
			...[1, 2, 3].map((n) => el("span", { class: n <= result.stars ? "star on" : "star" }, "★")),
		);
		this.show(
			"over",
			el(
				"div",
				{
					class: result.stats ? "screen over with-stats" : "screen over",
					role: "dialog",
					"aria-label": heading,
				},
				el("h1", {}, heading),
				el(
					"div",
					{ class: "over-summary" },
					nightmare ? null : stars,
					el(
						"p",
						{ class: "final-score" },
						nightmare ? `${result.score} ⭐ ⏱️ ${formatTime(nightmare.ms)}` : `${result.score} ⭐`,
					),
					nightmare
						? nightmareRecords(result.score, result.best, nightmare)
						: result.newBest
							? el("p", { class: "new-best" }, "🎉 Nytt rekord!")
							: el("p", { class: "best" }, `Rekord: ${result.best} ⭐`),
				),
				result.stats ? statsList(result.stats) : null,
				el("div", { class: "row over-buttons" }, again, menu),
			),
			null,
		);
		window.setTimeout(() => {
			again.disabled = false;
			menu.disabled = false;
			again.focus();
		}, OVER_BUTTON_DELAY_MS);
	}

	showGame(): void {
		this.screen?.remove();
		this.screen = null;
		this.hud.hidden = false;
		this.version.hidden = true;
		document.body.dataset.screen = "playing";
	}

	setScore(score: number): void {
		this.score.textContent = String(score);
	}

	/** The streak counter in the HUD; null hides it (Svit off). */
	setStreak(streak: number | null): void {
		this.streak.hidden = streak === null;
		const text = `🔥 ${streak ?? 0}`;
		if (this.streak.textContent !== text) this.streak.textContent = text;
	}

	/** "🔥 5 i rad! +1" over the picture for a moment. */
	showStreak(count: number, bonus: number): void {
		const pop = el(
			"div",
			{ class: "streak-pop", "aria-live": "polite" },
			`🔥 ${count} i rad! +${bonus}`,
		);
		this.root.append(pop);
		window.setTimeout(() => pop.remove(), 1400);
	}

	/** Mardröm's lives in the HUD; null hides them. */
	setLives(lives: number | null): void {
		this.lives.hidden = lives === null;
		const text = `❤️ ${lives ?? 0}`;
		if (this.lives.textContent !== text) this.lives.textContent = text;
		this.lives.toggleAttribute("data-low", lives !== null && lives <= 3);
	}

	/** Mardröm's clock: counts up, with a full bar. */
	setElapsed(ms: number): void {
		const shown = formatTime(ms);
		if (this.timeText.textContent !== shown) this.timeText.textContent = shown;
		this.timeBar.style.transform = "scaleX(1)";
		this.time.toggleAttribute("data-low", false);
		this.time.setAttribute("aria-label", "Tid");
	}

	/** `left` and `total` in seconds. */
	setTime(left: number, total: number): void {
		const shown = String(Math.ceil(left));
		if (this.timeText.textContent !== shown) this.timeText.textContent = shown;
		this.timeBar.style.transform = `scaleX(${total > 0 ? left / total : 0})`;
		this.time.toggleAttribute("data-low", left <= 5);
		this.time.setAttribute("aria-label", "Tid kvar");
	}

	/** The big "3, 2, 1, Kör!"; null hides it. */
	setCountdown(text: string | null): void {
		this.countdown.hidden = text === null;
		if (text !== null && this.countdown.textContent !== text) {
			this.countdown.textContent = text;
			// Restart the zoom animation for each new number.
			this.countdown.classList.remove("tick");
			void this.countdown.offsetWidth;
			this.countdown.classList.add("tick");
		}
	}

	private show(name: string, screen: HTMLElement, focus: HTMLElement | null): void {
		this.screen?.remove();
		this.screen = screen;
		this.hud.hidden = name !== "over";
		this.countdown.hidden = true;
		this.version.hidden = name !== "menu";
		this.root.append(screen);
		focus?.focus();
		document.body.dataset.screen = name;
	}
}

/** Mardröm's records under the result: new ones are celebrated. */
function nightmareRecords(
	score: number,
	bestScore: number,
	n: NonNullable<Result["nightmare"]>,
): HTMLElement {
	if (n.newTime && n.newScore)
		return el("p", { class: "new-best" }, "🎉 Nytt rekord i tid och poäng!");
	if (n.newTime) return el("p", { class: "new-best" }, "🎉 Ny rekordtid!");
	if (n.newScore && score > 0) return el("p", { class: "new-best" }, "🎉 Nytt poängrekord!");
	return el("p", { class: "best" }, `Rekord: ${formatTime(n.bestMs)} · ${bestScore} ⭐`);
}

function scoreTable(scores: readonly NightmareScore[]): HTMLElement {
	if (scores.length === 0) {
		return el("p", { class: "scores-empty" }, "Inga rundor än. Vågar du?");
	}
	const cell = (text: string) => el("td", {}, text);
	const head = (text: string) => el("th", { scope: "col" }, text);
	return el(
		"table",
		{ class: "scores-table" },
		el("thead", {}, el("tr", {}, head("#"), head("Tid"), head("Poäng"), head("Datum"))),
		el(
			"tbody",
			{},
			...scores.map((run, i) =>
				el(
					"tr",
					{},
					cell(String(i + 1)),
					cell(formatTime(run.ms)),
					cell(String(run.score)),
					cell(new Date(run.date).toLocaleDateString("sv-SE")),
				),
			),
		),
	);
}

/** The trick light as the game draws it: a green, spiky glow around a dark core. */
function trickIcon(): HTMLElement {
	return el("span", { class: "trick-icon", "aria-hidden": "true" });
}

/** The round's numbers on the game-over screen (Svit on). */
function statsList(stats: RoundStats): HTMLElement {
	const item = (label: string | Node, value: string) =>
		el("div", { class: "stat" }, el("dt", {}, label), el("dd", {}, value));
	return el(
		"dl",
		{ class: "stats", "aria-label": "Statistik" },
		item("🎯 Träffade lampor", stats.hitPercent === null ? "–" : `${stats.hitPercent} %`),
		item("🔥 Längsta svit", String(stats.longestStreak)),
		item("🌑 Missar", String(stats.misclicks)),
		...(stats.tricked === null
			? []
			: [item(el("span", {}, trickIcon(), " Trolllampor"), String(stats.tricked))]),
	);
}
